import { useEffect, useMemo, useState } from "react";
import { appConfirm } from "../../components/ConfirmDialog";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Search, Send, Trash2, Loader2, FileText, Clock, Eye, Skull, ExternalLink, RotateCcw, Zap, Mail, Plus } from "lucide-react";
import { ResendCoverPickerModal } from "./ResendCoverPickerModal";
import { LeadSourcePill, LeadSourceSummary } from "./LeadSource";
import { StagePill, STAGES } from "./pipeline";

const gbp = (n) => "£" + Math.round(Number(n || 0)).toLocaleString("en-GB");
const dateGB = (d) => d ? new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—";

export const TYPE_META = {
  prospect: { label: "Prospect", dot: "#2E6BB0", cls: "bg-[#DCE9F5] text-[#1D4ED8]" },
  booked: { label: "Booked · pre-survey", dot: "#C79A3B", cls: "bg-[#FBE3D6] text-[#8A3D0A]" },
  current: { label: "Current client", dot: "#1F6B3B", cls: "bg-[#D8EED8] text-[#1F6B3B]" },
  dead: { label: "Dead lead", dot: "#C0392B", cls: "bg-[#F8DCD7] text-[#C0392B]" },
};

function TypeBadge({ type }) {
  const m = TYPE_META[type] || TYPE_META.prospect;
  return (
    <span data-testid={`client-type-${type}`} className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-widest ${m.cls}`}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: m.dot }} /> {m.label}
    </span>
  );
}

function ResendHistoryStrip({ rowKey, history }) {
  const h = Array.isArray(history) ? history : [];
  if (!h.length) return null;
  const last = h[h.length - 1];
  const tip = h.slice().reverse().map(e => {
    const at = e?.at ? new Date(e.at).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";
    const hero = e?.hero && e.hero !== "default" ? ` · cover: ${e.hero}` : "";
    const msg = e?.message ? `\n   “${e.message}”` : "";
    return `${at} · ${e?.by || "unknown"}${hero}${msg}`;
  }).join("\n");
  return (
    <div data-testid={`client-resend-history-${rowKey}`}
      title={`Resend history:\n${tip}`}
      className="mt-1 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-[#8A6D2B] bg-[#FBF3EC] border border-[#F0D5B4] px-1.5 py-0.5">
      <Mail size={9} /> Resent {h.length}× · {last?.at ? dateGB(last.at) : "—"}
    </div>
  );
}

const FILTERS = [
  ["all", "All"], ["prospect", "Prospects"], ["booked", "Booked"],
  ["current", "Current clients"], ["dead", "Dead leads"], ["unpaid", "Unpaid"],
];

export function ClientsPanel({ role, bookings, loadBookings, setOpenRef, onCountsChange, filter, setFilter, onOpenQuote, reloadKey }) {
  const isSiteManager = role === "site_manager";
  const canDelete = role === "admin" || role === "senior_admin";
  const [prospectRows, setProspectRows] = useState([]);
  const [deadRows, setDeadRows] = useState([]);
  const [q, setQ] = useState("");
  const [stageFilter, setStageFilter] = useState(null);
  const [loading, setLoading] = useState(!isSiteManager);
  const [busy, setBusy] = useState({});
  const canBulk = role === "senior_admin";
  const [selected, setSelected] = useState([]);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [resendTarget, setResendTarget] = useState(null); // quote row currently being resent
  const [sourceFilter, setSourceFilter] = useState(null); // marketing channel filter

  const load = async () => {
    if (isSiteManager) return;
    setLoading(true);
    try {
      const [sq, dl] = await Promise.all([api.get("/admin/saved-quotes"), api.get("/admin/dead-leads")]);
      setProspectRows(sq.data.items || []);
      setDeadRows(dl.data.items || []);
    } catch { toast.error("Could not load clients"); }
    setLoading(false);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- refetch on mount and whenever a job file closes (reloadKey bumps)
  useEffect(() => { load(); }, [reloadKey]);
  const reload = () => { load(); loadBookings(); if (onCountsChange) onCountsChange(); };

  const rows = useMemo(() => {
    const out = [];
    for (const r of prospectRows) {
      const isBooking = r.kind === "booking";
      out.push({
        key: `${r.kind || "quote"}-${r.quote_id}`,
        kind: isBooking ? "booking" : "quote",
        type: isBooking ? "booked" : "prospect",
        quote_id: r.quote_id, reference: r.reference, route: r.route,
        name: r.name, email: r.email, postcode: r.postcode,
        total: r.total_inc_vat, created_at: r.created_at, age_days: r.age_days,
        stage: r.stage || (isBooking ? "booking" : "new"),
        status: r.booking_status || "", deletes_in_days: r.deletes_in_days,
        booking_fee_deferred: !!r.booking_fee_deferred,
        seen: !!r.seen_by_admin, expiring_sent: r.expiring_notice_sent,
        expiring_at: r.expiring_notice_sent_at, expiring_code: r.expiring_notice_code,
        start_date: r.start_date, site_visit_status: r.site_visit_status,
        site_visit_date: r.site_visit_date, site_visit_time: r.site_visit_time,
        estimate_status: r.estimate_status || "",
        estimate_accepted: !!r.estimate_accepted,
        followup_sent_at: r.followup_sent_at,
        followup_opened: !!r.followup_opened,
        resend_history: r.resend_history || [],
        lead_source: r.lead_source || null,
      });
    }
    for (const b of (bookings || []).filter(b => b.job_confirmed && !b.dead_lead && !b.deleted)) {
      out.push({
        key: `current-${b.reference}`, kind: "booking", type: "current",
        reference: b.reference, name: b.name, email: b.email, postcode: b.postcode,
        total: b.total_inc_vat, created_at: b.created_at, status: b.status,
        stage: b.stage || "confirmed", start_date: b.start_date, seen: true,
      });
    }
    for (const d of deadRows) {
      out.push({
        key: `dead-${d.kind}-${d.id_or_ref}`, kind: d.kind, type: "dead",
        quote_id: d.kind === "quote" ? d.id_or_ref : undefined, id_or_ref: d.id_or_ref,
        reference: d.reference, name: d.name, email: d.email, postcode: d.postcode,
        total: d.total_inc_vat, created_at: d.created_at, dead_at: d.dead_at, dead_by: d.dead_by,
        purge_in_days: d.purge_in_days, seen: true,
      });
    }
    return out.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
  }, [prospectRows, deadRows, bookings]);

  // Pipeline snapshot: counts + £ value per live stage across (non-dead) prospects & booked
  const snapshot = useMemo(() => {
    const live = rows.filter(r => r.type === "prospect" || r.type === "booked");
    const byStage = STAGES.slice(0, 5).map(s => {
      const inStage = live.filter(r => r.stage === s.key);
      return { ...s, count: inStage.length, value: inStage.reduce((a, r) => a + Number(r.total || 0), 0) };
    });
    const current = rows.filter(r => r.type === "current");
    const dead = rows.filter(r => r.type === "dead");
    return { byStage, current: { count: current.length, value: current.reduce((a, r) => a + Number(r.total || 0), 0) }, dead: { count: dead.length } };
  }, [rows]);

  const ql = q.trim().toLowerCase();
  const visible = useMemo(() => rows
    .filter(r => {
      if (filter === "all") return true;
      if (filter === "unpaid") return r.status === "pending_payment";
      return r.type === filter;
    })
    .filter(r => !stageFilter || ((r.type === "prospect" || r.type === "booked") && r.stage === stageFilter))
    .filter(r => !sourceFilter || r.lead_source?.channel === sourceFilter)
    .filter(r => !ql || (`${r.reference || ""} ${r.name || ""} ${r.email || ""} ${r.postcode || ""}`).toLowerCase().includes(ql)),
  [rows, filter, stageFilter, sourceFilter, ql]);

  const openRow = (r) => {
    if (r.kind === "booking") { setOpenRef(r.reference); }
    else { if (onOpenQuote) onOpenQuote(r.quote_id); if (!r.seen) markSeen(r.quote_id); }
  };

  const markSeen = async (qid) => {
    setProspectRows(rs => rs.map(r => r.quote_id === qid ? { ...r, seen_by_admin: true } : r));
    try { await api.post(`/admin/saved-quotes/${qid}/mark-seen`); if (onCountsChange) onCountsChange(); } catch { /* keep optimistic */ }
  };

  const markDead = async (r) => {
    if (!(await appConfirm({ title: `Move ${r.name || r.reference} to Dead leads?`, description: "No further automated emails will be sent to them. Auto-purged after 60 days — restore anytime before then.", confirmLabel: "Mark dead", destructive: true }))) return;
    setBusy(b => ({ ...b, [r.key]: "dead" }));
    try {
      if (r.kind === "booking") await api.post(`/admin/booking/${encodeURIComponent(r.reference)}/mark-dead`);
      else await api.post(`/admin/saved-quotes/${r.quote_id}/mark-dead`);
      toast.success("Moved to Dead leads — automated emails stopped");
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not mark as dead"); }
    setBusy(b => ({ ...b, [r.key]: null }));
  };

  const restore = async (r) => {
    setBusy(b => ({ ...b, [r.key]: "restore" }));
    try {
      await api.post("/admin/dead-lead/restore", { kind: r.kind, id_or_ref: r.id_or_ref });
      toast.success("Restored to the live pipeline");
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not restore"); }
    setBusy(b => ({ ...b, [r.key]: null }));
  };

  const sendExpiring = async (r) => {
    setBusy(b => ({ ...b, [r.key]: "sending" }));
    try {
      const res = await api.post(`/admin/saved-quotes/${r.quote_id}/send-expiring`);
      toast.success(res.data.sent ? `Re-quote sent · code ${res.data.code || "(no promo)"}` : "Could not send email — check Gmail connection");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send"); }
    setBusy(b => ({ ...b, [r.key]: null }));
  };

  const resendQuote = (r) => {
    if (!r.quote_id) return;
    if (!r.email) { toast.error("No email on this quote — add one first"); return; }
    setResendTarget(r);
  };

  const del = async (r) => {
    if (!(await appConfirm({ title: "Permanently delete this saved quote?", description: "This cannot be undone.", confirmLabel: "Delete", destructive: true }))) return;
    setBusy(b => ({ ...b, [r.key]: "deleting" }));
    try { await api.delete(`/admin/saved-quotes/${r.quote_id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not delete"); }
    setBusy(b => ({ ...b, [r.key]: null }));
  };

  const toggleSel = (key) => setSelected(s => s.includes(key) ? s.filter(k => k !== key) : [...s, key]);
  const bulkDelete = async () => {
    const chosen = rows.filter(r => selected.includes(r.key));
    if (!chosen.length) return;
    const nQuotes = chosen.filter(r => r.kind === "quote").length;
    const nJobs = chosen.length - nQuotes;
    if (!(await appConfirm({
      title: `Delete ${chosen.length} selected client${chosen.length > 1 ? "s" : ""}?`,
      description: `${nQuotes ? `${nQuotes} quote(s) will be PERMANENTLY deleted (cannot be recovered). ` : ""}${nJobs ? `${nJobs} job file(s) will move to Trash (restorable by senior admin). ` : ""}Deleted clients receive no further automated emails.`,
      confirmLabel: "Delete selected", destructive: true,
    }))) return;
    setBulkBusy(true);
    try {
      const items = chosen.map(r => r.kind === "quote"
        ? { kind: "quote", id_or_ref: r.quote_id || r.id_or_ref }
        : { kind: "booking", id_or_ref: r.reference });
      const res = await api.post("/admin/clients/bulk-delete", { items });
      toast.success(`Deleted ${res.data.deleted_quotes} quote(s) · ${res.data.trashed_bookings} job file(s) moved to Trash`);
      setSelected([]);
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Bulk delete failed"); }
    setBulkBusy(false);
  };
  const allVisibleSelected = visible.length > 0 && visible.every(r => selected.includes(r.key));
  const toggleAll = () => setSelected(allVisibleSelected ? [] : visible.map(r => r.key));

  return (
    <div data-testid="clients-panel">
      <div className="flex items-end justify-between gap-3 flex-wrap mb-4">
        <div>
          <div className="label-eyebrow">One list, whole business</div>
          <h2 className="text-2xl font-black text-[#12263A]">Clients</h2>
          <p className="text-sm text-[#6B7280] mt-1 max-w-3xl">
            Everyone in one place — <b className="text-[#1D4ED8]">prospects</b>, <b className="text-[#8A3D0A]">booked (pre-survey)</b>,{" "}
            <b className="text-[#1F6B3B]">current clients</b> and <b className="text-[#C0392B]">dead leads</b>. Dead leads receive no automated emails.
          </p>
        </div>
        <div className="relative w-full sm:w-72 flex items-center gap-2">
          {!isSiteManager && (
            <a href="/quote" target="_blank" rel="noreferrer" data-testid="clients-new-quote-as-admin"
              title="Open the configurator to build a fresh quote for a client — postcode & contact gate skipped"
              className="inline-flex items-center gap-1.5 shrink-0 text-xs font-bold uppercase tracking-widest bg-[#C79A3B] text-white px-3 py-2 hover:bg-[#A87E28] whitespace-nowrap">
              <Plus size={12} /> New quote
            </a>
          )}
          <div className="relative flex-1">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
            <input data-testid="clients-search" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search name, email, postcode, reference…"
              className="w-full border border-[#E5E7EB] pl-9 pr-3 py-2 text-sm" />
          </div>
        </div>
      </div>

      {!isSiteManager && (
        <div className="flex flex-wrap gap-2 mb-4" data-testid="clients-pipeline-snapshot">
          {snapshot.byStage.map(s => (
            <button key={s.key} data-testid={`snapshot-stage-${s.key}`}
              onClick={() => { setStageFilter(f => f === s.key ? null : s.key); setFilter("all"); }}
              className={`border bg-white px-3 py-2 text-left hover:bg-[#FBF3EC] transition-colors ${stageFilter === s.key ? "border-[#C79A3B] bg-[#FBF3EC]" : "border-[#E5E7EB]"}`}>
              <div className="text-[10px] uppercase tracking-widest text-[#6B7280] font-bold">{s.label}</div>
              <div className="text-lg font-black text-[#12263A] leading-tight">{s.count} <span className="text-[11px] font-bold text-[#6B7280]">· {gbp(s.value)}</span></div>
            </button>
          ))}
          <button data-testid="snapshot-current" onClick={() => { setStageFilter(null); setFilter("current"); }}
            className={`border bg-white px-3 py-2 text-left hover:bg-[#FBF3EC] transition-colors ${filter === "current" ? "border-[#1F6B3B]" : "border-[#E5E7EB]"}`}>
            <div className="text-[10px] uppercase tracking-widest text-[#1F6B3B] font-bold">Current clients</div>
            <div className="text-lg font-black text-[#12263A] leading-tight">{snapshot.current.count} <span className="text-[11px] font-bold text-[#6B7280]">· {gbp(snapshot.current.value)}</span></div>
          </button>
          <button data-testid="snapshot-dead" onClick={() => { setStageFilter(null); setFilter("dead"); }}
            className={`border bg-white px-3 py-2 text-left hover:bg-[#FBF3EC] transition-colors ${filter === "dead" ? "border-[#C0392B]" : "border-[#E5E7EB]"}`}>
            <div className="text-[10px] uppercase tracking-widest text-[#C0392B] font-bold">Dead leads</div>
            <div className="text-lg font-black text-[#12263A] leading-tight">{snapshot.dead.count}</div>
          </button>
        </div>
      )}

      {!isSiteManager && <LeadSourceSummary rows={rows} active={sourceFilter} onPick={setSourceFilter} />}

      <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="clients-filter-bar">
        {FILTERS.filter(([v]) => !isSiteManager || v === "all" || v === "current").map(([v, l]) => {
          const n = v === "all" ? rows.length : v === "unpaid" ? rows.filter(r => r.status === "pending_payment").length : rows.filter(r => r.type === v).length;
          const active = filter === v && !stageFilter;
          return (
            <button key={v} data-testid={`clients-filter-${v}`} onClick={() => { setFilter(v); setStageFilter(null); }}
              className={`px-3 py-1 text-xs font-bold uppercase tracking-wider border inline-flex items-center gap-1.5 transition-colors ${active ? "bg-[#12263A] text-white border-[#12263A]" : "bg-white border-[#E5E7EB] text-[#6B7280] hover:border-[#12263A]"}`}>
              {TYPE_META[v] && <span className="w-1.5 h-1.5 rounded-full" style={{ background: TYPE_META[v].dot }} />}
              {l}
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full leading-none ${active ? "bg-white/20 text-white" : "bg-[#F0EAE0] text-[#6B7280]"}`}>{n}</span>
            </button>
          );
        })}
        {stageFilter && (
          <button data-testid="clients-stage-filter-clear" onClick={() => setStageFilter(null)}
            className="px-3 py-1 text-xs font-bold uppercase tracking-wider border bg-[#FBF3EC] border-[#C79A3B] text-[#12263A]">
            Stage: {STAGES.find(s => s.key === stageFilter)?.label} ✕
          </button>
        )}
      </div>

      {canBulk && selected.length > 0 && (
        <div className="flex items-center gap-3 flex-wrap mb-3 border border-[#C0392B] bg-[#FEF6F5] px-3 py-2" data-testid="clients-bulk-bar">
          <span className="text-xs font-bold text-[#C0392B]" data-testid="clients-bulk-count">{selected.length} selected</span>
          <button data-testid="clients-bulk-delete" onClick={bulkDelete} disabled={bulkBusy}
            className="inline-flex items-center gap-1.5 text-xs font-bold bg-[#C0392B] text-white px-3 py-1.5 hover:bg-[#A03024] disabled:opacity-50">
            {bulkBusy ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />} Delete selected
          </button>
          <span className="text-[11px] text-[#6B7280]">Quotes are removed permanently · job files go to Trash · no further automated emails</span>
          <button data-testid="clients-bulk-clear" onClick={() => setSelected([])} className="text-xs font-bold text-[#6B7280] hover:text-[#12263A] ml-auto">Clear</button>
        </div>
      )}

      {loading ? (
        <div className="py-10 text-center text-sm text-[#6B7280]"><Loader2 className="animate-spin inline mr-2" size={14} /> Loading clients…</div>
      ) : visible.length === 0 ? (
        <div className="py-14 text-center text-sm text-[#9CA3AF] border border-dashed border-[#E5E7EB]" data-testid="clients-empty">
          <FileText size={22} className="mx-auto mb-2 opacity-40" />
          {ql ? "No clients match that search." : "Nothing in this view yet."}
        </div>
      ) : (
        <div className="border border-[#E5E7EB] bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#F8F6F2] text-left text-[11px] uppercase tracking-widest text-[#6B7280]">
                {canBulk && (
                  <th className="px-3 py-2.5 w-8">
                    <input type="checkbox" data-testid="clients-select-all" checked={allVisibleSelected} onChange={toggleAll}
                      className="accent-[#C0392B] cursor-pointer" title="Select all visible" />
                  </th>
                )}
                <th className="px-3 py-2.5">Reference</th>
                <th className="px-3 py-2.5">Client</th>
                <th className="px-3 py-2.5">Type</th>
                <th className="px-3 py-2.5">Stage / status</th>
                <th className="px-3 py-2.5 text-right">Total</th>
                <th className="px-3 py-2.5">Created</th>
                <th className="px-3 py-2.5">Next</th>
                <th className="px-3 py-2.5 text-right"></th>
              </tr>
            </thead>
            <tbody>
              {visible.map(r => (
                <tr key={r.key} data-testid={`client-row-${r.key}`} className={`border-t border-[#E5E7EB] hover:bg-[#FBF3EC] ${!r.seen ? "bg-[#FBF3EC]/60" : ""} ${selected.includes(r.key) ? "bg-[#FEF6F5]" : ""}`}>
                  {canBulk && (
                    <td className="px-3 py-2.5">
                      <input type="checkbox" data-testid={`client-select-${r.key}`} checked={selected.includes(r.key)}
                        onChange={() => toggleSel(r.key)} className="accent-[#C0392B] cursor-pointer" />
                    </td>
                  )}
                  <td className="px-3 py-2.5 font-black text-[#C79A3B]">
                    <div className="flex items-center gap-2 flex-wrap">
                      <button data-testid={`client-ref-${r.key}`} onClick={() => openRow(r)}
                        className="font-black text-[#C79A3B] hover:text-[#A87E28] hover:underline underline-offset-2 decoration-2"
                        title={r.kind === "booking" ? "Open job file" : "Open quote file"}>
                        {r.reference || "—"}
                      </button>
                      {!r.seen && <span className="inline-block px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest bg-[#C79A3B] text-white rounded-sm">NEW</span>}
                    </div>
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="font-medium text-[#12263A]">{r.name || "—"}</div>
                    <div className="text-xs text-[#6B7280]">{r.email || "no email"}{r.postcode ? ` · ${r.postcode}` : ""}</div>
                    {r.lead_source && <div className="mt-1"><LeadSourcePill source={r.lead_source} rowKey={r.key} /></div>}
                  </td>
                  <td className="px-3 py-2.5"><TypeBadge type={r.type} /></td>
                  <td className="px-3 py-2.5">
                    {r.type === "dead"
                      ? <span className="text-xs text-[#6B7280]">Dead {dateGB(r.dead_at)}{r.dead_by ? ` · ${r.dead_by}` : ""}</span>
                      : r.type === "current"
                        ? <span className="text-xs">{(r.status || "").replace(/_/g, " ")}{r.status === "pending_payment" && <b className="text-[#C0392B]"> · unpaid</b>}</span>
                        : <span className="inline-flex items-center gap-1.5 flex-wrap">
                            <StagePill stage={r.stage} />
                            {r.status === "site_visit_booked" && (
                              <span data-testid={`client-site-visit-${r.key}`} title="Free site visit booked online (autumn offer — no booking fee taken)"
                                className="inline-block px-1.5 py-0.5 rounded-sm text-[9px] font-black uppercase tracking-widest bg-[#EADBF3] text-[#5B2A83] border border-[#D9C3EA]">
                                Visit {r.site_visit_date ? dateGB(r.site_visit_date) : "TBC"}{r.site_visit_time ? ` · ${r.site_visit_time}` : ""}
                              </span>
                            )}
                            {r.booking_fee_deferred && (
                              <span data-testid={`client-fee-deferred-${r.key}`}
                                className="inline-block px-1.5 py-0.5 rounded-sm text-[9px] font-black uppercase tracking-widest bg-[#FBF3EC] text-[#A87E28] border border-[#F0D5B4]">
                                £499 deferred
                              </span>
                            )}
                            {r.estimate_accepted && (
                              <span data-testid={`client-estimate-accepted-${r.key}`}
                                className="inline-block px-1.5 py-0.5 rounded-sm text-[9px] font-black uppercase tracking-widest bg-[#D8EED8] text-[#1F6B3B]">
                                Accepted ✓
                              </span>
                            )}
                            {r.followup_opened && (
                              <span data-testid={`client-followup-opened-${r.key}`} title={`Follow-up email opened`}
                                className="inline-block px-1.5 py-0.5 rounded-sm text-[9px] font-black uppercase tracking-widest bg-[#E3EDF7] text-[#2E6BB0] border border-[#C4D9EE]">
                                Opened ✓
                              </span>
                            )}
                            {(r.estimate_status === "invoice" || r.estimate_status === "paid") && (
                              <span data-testid={`client-estimate-status-${r.key}`}
                                className={`inline-block px-1.5 py-0.5 rounded-sm text-[9px] font-black uppercase tracking-widest ${r.estimate_status === "paid" ? "bg-[#1F6B3B] text-white" : "bg-[#C79A3B] text-white"}`}>
                                {r.estimate_status}
                              </span>
                            )}
                          </span>}
                  </td>
                  <td className="px-3 py-2.5 text-right tabular-nums">{gbp(r.total)}</td>
                  <td className="px-3 py-2.5 text-xs text-[#6B7280]">{dateGB(r.created_at)}{r.age_days != null ? ` · ${r.age_days}d` : ""}</td>
                  <td className="px-3 py-2.5 text-xs">
                    {r.type === "dead" && <span className={`inline-flex items-center gap-1 font-bold ${r.purge_in_days <= 7 ? "text-[#C0392B]" : "text-[#6B7280]"}`}><Clock size={11} /> purge {r.purge_in_days}d</span>}
                    {r.type === "current" && <span className="text-[#6B7280]">Start {r.start_date ? dateGB(r.start_date) : "—"}</span>}
                    {r.type === "booked" && <span className="text-[#6B7280]">Survey {r.site_visit_status || "not scheduled"}</span>}
                    {r.type === "prospect" && (
                      r.expiring_sent
                        ? <span className="text-[#1F6B3B] font-bold">Re-quote sent {dateGB(r.expiring_at)}</span>
                        : <span className={`inline-flex items-center gap-1 font-bold ${r.deletes_in_days <= 60 ? "text-[#C0392B]" : "text-[#6B7280]"}`}><Clock size={11} /> deletes {r.deletes_in_days}d</span>
                    )}
                    <ResendHistoryStrip rowKey={r.key} history={r.resend_history} />
                  </td>
                  <td className="px-3 py-2.5 text-right whitespace-nowrap">
                    {!r.seen && (
                      <button data-testid={`client-mark-seen-${r.key}`} onClick={() => markSeen(r.quote_id)}
                        className="inline-flex items-center gap-1.5 text-xs font-bold text-[#6B7280] border border-[#E5E7EB] px-2.5 py-1 hover:border-[#C79A3B] mr-1.5">
                        <Eye size={12} /> Seen
                      </button>
                    )}
                    <button data-testid={`client-open-${r.key}`} onClick={() => openRow(r)}
                      className="inline-flex items-center gap-1.5 text-xs font-bold text-[#12263A] border border-[#E5E7EB] px-2.5 py-1 hover:border-[#C79A3B]">
                      <ExternalLink size={12} /> Open
                    </button>
                    {r.type === "prospect" && (
                      <button data-testid={`client-requote-${r.key}`} onClick={() => sendExpiring(r)}
                        disabled={!r.email || busy[r.key] === "sending"}
                        title={r.email ? "Sends the client an automatic re-quote email with 10% off" : "No email on file"}
                        className="inline-flex items-center gap-1.5 text-xs font-bold text-[#12263A] border border-[#E5E7EB] px-2.5 py-1 hover:border-[#C79A3B] ml-1.5 disabled:opacity-40">
                        {busy[r.key] === "sending" ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />} Re-quote <Zap size={10} className="fill-[#F59E0B] text-[#B45309]" />
                      </button>
                    )}
                    {r.type === "prospect" && r.quote_id && (
                      <button data-testid={`client-resend-quote-${r.key}`} onClick={() => resendQuote(r)}
                        disabled={!r.email}
                        title={r.email ? "Re-send the client their itemised quote email — pick which visual leads" : "No email on file"}
                        className="inline-flex items-center gap-1.5 text-xs font-bold text-[#12263A] border border-[#E5E7EB] px-2.5 py-1 hover:border-[#C79A3B] ml-1.5 disabled:opacity-40">
                        <Mail size={12} /> Resend
                      </button>
                    )}
                    {(r.type === "prospect" || r.type === "booked") && (
                      <button data-testid={`client-mark-dead-${r.key}`} onClick={() => markDead(r)}
                        disabled={busy[r.key] === "dead"}
                        title="Move to Dead leads — stops all automated emails"
                        className="inline-flex items-center gap-1.5 text-xs font-bold text-[#6B7280] border border-[#E5E7EB] px-2.5 py-1 hover:border-[#C0392B] hover:text-[#C0392B] ml-1.5 disabled:opacity-40">
                        {busy[r.key] === "dead" ? <Loader2 size={12} className="animate-spin" /> : <Skull size={12} />} Dead
                      </button>
                    )}
                    {r.type === "dead" && (
                      <button data-testid={`client-restore-${r.key}`} onClick={() => restore(r)}
                        disabled={busy[r.key] === "restore"}
                        className="inline-flex items-center gap-1.5 text-xs font-bold text-[#12263A] border border-[#E5E7EB] px-2.5 py-1 hover:border-[#1F6B3B] ml-1.5 disabled:opacity-40">
                        {busy[r.key] === "restore" ? <Loader2 size={12} className="animate-spin" /> : <RotateCcw size={12} />} Restore
                      </button>
                    )}
                    {r.type === "prospect" && canDelete && (
                      <button data-testid={`client-delete-${r.key}`} onClick={() => del(r)}
                        disabled={busy[r.key] === "deleting"}
                        className="inline-flex items-center gap-1.5 text-xs font-bold text-[#C0392B] border border-[#F1D4CE] px-2.5 py-1 hover:border-[#C0392B] ml-1.5 disabled:opacity-40">
                        {busy[r.key] === "deleting" ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <ResendCoverPickerModal open={!!resendTarget} quote={resendTarget}
        onClose={() => setResendTarget(null)}
        onSent={() => load()} />
    </div>
  );
}
